import { Accreditation } from "constants/draftingAttributes";
import { IGNORED_DRAFTING_ATTRIBUTES } from "constants/saleLots";
import { Species } from "constants/species";

// Keys defaulted to Accreditation.NONE for a brand-new sale lot, before any
// consignment / property / template overrides are applied.
const ACCREDITATION_DEFAULT_KEYS = [
  "accreditationAntibioticFree",
  "accreditationBackgrounded",
  "accreditationEU",
  "accreditationGrassFed",
  "accreditationOrganic",
  "accreditationPCAS",
  "accreditationPTE",
  "accreditationPTIC",
  "b12Vac",
  "backline",
  "drenched",
  "feeder",
  "gudairApproved",
  "MAndTS",
  "notStationMated",
  "painRelief",
  "SAEligible",
  "sixInOne",
  "stationMated",
] as const;

export interface CopiedOrPreviousSaleLotValues {
  sale_round_id?: SaleLot["sale_round_id"] | null;
  breed_id?: SaleLot["breed_id"];
  sex_id?: SaleLot["sex_id"];
  age_id?: SaleLot["age_id"];
  exemption_id?: SaleLot["exemption_id"];
  pricing_type_id?: SaleLot["pricing_type_id"];
  draftingAttributes: Partial<DraftingAttributes>;
}

const NONE_ACCREDITATIONS: Partial<DraftingAttributes> =
  ACCREDITATION_DEFAULT_KEYS.reduce((acc, k) => {
    acc[k] = Accreditation.NONE;
    return acc;
  }, {});

export function getConsignmentAccreditationDefaults(
  consignment: Consignment | null | undefined,
): Partial<DraftingAttributes> {
  const result = {};
  const consignmentDraftingAttributes = consignment?.draftingAttributes;
  if (!consignmentDraftingAttributes) return result;
  for (const key of ACCREDITATION_DEFAULT_KEYS) {
    const value = consignmentDraftingAttributes[key];
    if (value === Accreditation.NONE || value === Accreditation.ALL) {
      result[key] = value;
    }
  }
  return result;
}

function getPropertyAccreditationDefaults(
  consignment: Consignment | null | undefined,
  speciesId: number | null | undefined,
) {
  const propertyAccreditationDefaults: Partial<DraftingAttributes> = {};
  if (consignment?.vendor_property && speciesId === Species.CATTLE) {
    if (consignment.vendor_property.accreditationEu !== null) {
      propertyAccreditationDefaults.accreditationEU = consignment
        .vendor_property.accreditationEu
        ? Accreditation.ALL
        : Accreditation.NONE;
    }
    if (consignment.vendor_property.accreditationPcas !== null) {
      propertyAccreditationDefaults.accreditationPCAS = consignment
        .vendor_property.accreditationPcas
        ? Accreditation.ALL
        : Accreditation.NONE;
    }
    if (consignment.vendor_property.accreditationJbas !== null) {
      propertyAccreditationDefaults.accreditationJBAS =
        consignment.vendor_property.accreditationJbas;
    }
  }
  return propertyAccreditationDefaults;
}

function getTemplateDraftingAttributes(
  template: SaleLot | null | undefined,
  consignmentAccreditationDefaults: Partial<DraftingAttributes>,
): Partial<DraftingAttributes> {
  const advancedDraftingDefaults: Partial<DraftingAttributes> = {};
  const sourceDraftingAttributes = template?.draftingAttributes;

  if (!sourceDraftingAttributes) {
    return advancedDraftingDefaults;
  }

  for (const [key, value] of Object.entries(sourceDraftingAttributes)) {
    // Skip per-animal counts/identifiers that shouldn't bleed across lots.
    if (IGNORED_DRAFTING_ATTRIBUTES.has(key)) {
      continue;
    }
    // Don't let a stale template override a fresh consignment default.
    if (key in consignmentAccreditationDefaults) {
      continue;
    }
    advancedDraftingDefaults[key] = value;
  }
  return advancedDraftingDefaults;
}

interface CopiedOrPreviousSaleLotValuesArgs {
  isNew: boolean;
  saleLot?: SaleLot | null;
  copiedSaleLot?: SaleLot | null;
  previousSaleLot?: SaleLot | null;
  consignment?: Consignment | null;
  speciesId?: number | null;
}

/**
 * Builds the initial draftingAttributes (and a few top-level carry-over fields)
 * for a sale lot. Each layer below overrides the one above:
 *
 *   1. Default everything to "NONE" accreditation.
 *   2. For new cattle lots, layer on the vendor property's defaults.
 *   3. Layer on the consignment's defaults.
 *   4. If editing, keep what's already on the sale lot. If new, clear vendorBredOverride.
 *   5. If new and there's a copied lot or a previous lot in this consignment,
 *      copy its drafting fields.
 */
export function resolveCopiedOrPreviousSaleLotValues({
  isNew,
  saleLot,
  copiedSaleLot,
  previousSaleLot,
  consignment,
  speciesId,
}: CopiedOrPreviousSaleLotValuesArgs): CopiedOrPreviousSaleLotValues {
  const consignmentAccreditationDefaults: Partial<DraftingAttributes> =
    getConsignmentAccreditationDefaults(consignment);

  const propertyAccreditationDefaults = isNew
    ? getPropertyAccreditationDefaults(consignment, speciesId)
    : {};

  const template = isNew ? copiedSaleLot || previousSaleLot : null;

  const baseDraftingAttributes: Partial<DraftingAttributes> =
    saleLot?.draftingAttributes
      ? saleLot.draftingAttributes
      : // Note that this is different to NONE - "unset" is a valid value for this, meaning refer to the consignment, or in turn the NVD,
        // whereas the others are essentially on or off with a default loaded from [somewhere]
        { vendorBredOverride: null };

  // prefill drafting attributes from the chosen template (copied or previous)
  const templateDraftingAttributes = getTemplateDraftingAttributes(
    template,
    consignmentAccreditationDefaults,
  );

  const draftingAttributes: Partial<DraftingAttributes> = {
    ...NONE_ACCREDITATIONS,
    ...propertyAccreditationDefaults,
    ...consignmentAccreditationDefaults,
    ...baseDraftingAttributes,
    ...templateDraftingAttributes,
  };

  const copiedTopLevel: Partial<CopiedOrPreviousSaleLotValues> = template
    ? {
        sale_round_id: template.sale_round_id,
        breed_id: template.breed_id,
        sex_id: template.sex_id,
        age_id: template.age_id,
        exemption_id: template.exemption_id,
        pricing_type_id: template.pricing_type_id,
      }
    : {};

  return {
    ...copiedTopLevel,
    draftingAttributes,
  };
}
